/////////////////////////////////////main code

import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { ArrowLeft, CheckIcon, RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSWAssessment } from './SWAssessmentContext';
import { decodedToken } from '../../utils/auth';
import { getLatestSWChangeListForVerification, getSWImportChangeListById, getCombinedSWChangeListsForImport , cacheManager} from '../../services/apiService';

const SWChangeVerification = () => {
  // Add this ref at the top of the component (outside useEffects)
  const navigationHandledRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState(null);
  const [loadingError, setLoadingError] = useState('');
  const [selectedSubType, setSelectedSubType] = useState({});
  const [showSubTypeDropdown, setShowSubTypeDropdown] = useState({});
  const [subTypeDataStatus, setSubTypeDataStatus] = useState({});
  const [subTypeStatus, setSubTypeStatus] = useState({});

  const {
    currentTab,
    setCurrentTab,
    setLoading,
    setUserId: setContextUserId,
    setChangeList,
    setSelectedChanges,
    setReferenceModel,
    setCompletedItem,
    getCurrentTabData,
    setDataLoadAttempted,
    getTabData,
    isAllCompleted,
    hasChangeList,
    isDataLoaded,
    isLoading,
    hasDataLoadAttempted,
    getDataSource,
    clearTabData,
    clearAllData,
  } = useSWAssessment();

  const tabs = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD', hasSubTypes: true },
    { id: 'AVA', label: 'AVA', hasSubTypes: true },
  ];
  const subProductTypes = {
    PD: [
      { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
      { id: 'PD_RGB LED', label: 'PD_RGB LED' },
      { id: 'PD_AIO', label: 'PD_AIO' },
      { id: 'PD_EPD', label: 'PD_EPD' }
    ],
    AVA: [
      { id: 'AVA_Audio', label: 'AVA_Audio' },
      { id: 'AVA_Headphone', label: 'AVA_Headphone' }
    ]
  };

  const assessmentItems = ['SW Change Item List'];
  const currentTabData = getCurrentTabData();

  // Get current sub-type for active tab
  const currentSubType = useMemo(() => {
    if (!tabs.find(t => t.id === currentTab)?.hasSubTypes) {
      return null;
    }
    return selectedSubType[currentTab] || subProductTypes[currentTab]?.[0]?.id;
  }, [currentTab, selectedSubType]);

  console.log('CURRENT TAB', currentTab);

  console.log(
    'CURRENT TAB DATA',
    currentTabData
  );

  console.log(
    'SELECTED CHANGES',
    currentTabData?.selectedChanges
  );

  // Check if at least one change is selected
  const hasSelectedChanges = useMemo(() => {
    if (!currentTabData?.selectedChanges) return false;
    return Object.values(currentTabData.selectedChanges).some(
      arr => Array.isArray(arr) && arr.length > 0
    );
  }, [currentTabData?.selectedChanges]);


const isReviewEnabled = useMemo(() => {
  const dataLoaded = isDataLoaded(currentTab);
  const loading = isLoading(currentTab);

  const hasData =
    !!currentTabData?.changeListData &&
    Object.keys(currentTabData.changeListData).length > 0;

  return (
    dataLoaded &&
    !loading &&
    hasData &&
    hasSelectedChanges
  );
}, [
  currentTab,
  currentTabData?.changeListData,
  hasSelectedChanges,
  isDataLoaded,
  isLoading
]);

  const hasItemData = useCallback(() => {
    return !!(currentTabData?.changeListData);
  }, [currentTabData?.changeListData]);

  // Get userId from JWT
  useEffect(() => {
    try {
      const decoded = decodedToken();
      if (decoded?.id) {
        setUserId(decoded.id);
        setContextUserId(decoded.id);
      } else {
        setLoadingError('User authentication required');
      }
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      setLoadingError('User authentication required');
    }
  }, [setContextUserId]);

  // Initialize default tab
  useEffect(() => {
    if (!tabs.find(t => t.id === currentTab)) {
      setCurrentTab('TV');
    }
  }, []);

  // Initialize default sub-types for PD and AVA
  useEffect(() => {
    const newSelectedSubType = {};
    tabs.forEach(tab => {
      if (tab.hasSubTypes && subProductTypes[tab.id] && !selectedSubType[tab.id]) {
        newSelectedSubType[tab.id] = subProductTypes[tab.id][0].id;
      }
    });
    if (Object.keys(newSelectedSubType).length > 0) {
      setSelectedSubType(prev => ({ ...prev, ...newSelectedSubType }));
    }
  }, []);
  const hasTabData = (tabId) => {
  const tabData = getTabData(tabId);

  return (
    tabData?.changeListData &&
    tabData?.changeListId
  );
};

useEffect(() => {
  const existingTabData = getTabData(currentTab);
  const isForceDefault   = location.state?.forceDefaultTemplate === true;
  const isNewUpload      = location.state?.newUpload === true;
  const isExplicitSelect = location.state?.returnFromImport === true &&
                           !!location.state?.changeListId &&
                           !isForceDefault &&
                           !isNewUpload;

  if (
    isExplicitSelect ||
    (
      !isForceDefault &&
      !isNewUpload &&
      existingTabData?.changeListData &&
      existingTabData?.selectedChanges &&
      Object.keys(existingTabData.selectedChanges).length > 0
    )
  ) {
    console.log(
      `Skipping loadDataForTab — ${isExplicitSelect ? 'explicit file selection handled by navigation effect' : 'context already has data'}`
    );
    return;
  }

  const loadDataForTab = async () => {
    if (!userId || !currentTab) return;

    const activeTabFromState = location.state?.activeTab;

    if (
      location.state?.returnFromImport &&
      !isForceDefault &&
      !isNewUpload &&
      location.state?.changeListId &&
      (activeTabFromState === currentTab || !activeTabFromState)
    ) {
      console.log('Skipping loadDataForTab — import handled by navigation effect');
      return;
    }

    const effectiveProductType = currentSubType || currentTab;

    try {
      const decoded = decodedToken();
      const userEmail = decoded?.email ?? '';
      const userType  = decoded?.userType ?? 'Admin';
      if (!userEmail || !userType) throw new Error('User email and type required');

      // Bust cache whenever a fresh upload just happened
      if (isForceDefault || isNewUpload) {
        console.log('Busting SW cache before re-fetch');
        cacheManager.invalidateSWProduct(effectiveProductType, userEmail);
      }

      console.log(`Loading SW data for ${effectiveProductType}`);

      if (location.state?.forceDefaultTemplate) {
        console.log('Loading DEFAULT template');

        const result = await getCombinedSWChangeListsForImport(
          effectiveProductType, userEmail, userType
        );

        if (result?.success && result?.defaultTemplate?.id) {
          const templateResult = await getSWImportChangeListById(
            result.defaultTemplate.id, userEmail, userType
          );

          if (templateResult?.success && templateResult?.data) {
            const { metadata, data } = templateResult;
            const swRows = Array.isArray(data) ? data : (data?.SW ?? []);

            const preSelected = swRows
              .filter(r => String(r.applied || '').trim().toUpperCase() === 'YES')
              .map(r => ({ id: r.id, detail: r.detail, applied: true }));

            setChangeList(
              currentTab, metadata.id, { SW: swRows },
              { SW: preSelected }, 'default',
              metadata?.lastModified || null, metadata?.uploadDate || null
            );
            return;
          }
        }
      }

      const result = await getLatestSWChangeListForVerification(
        effectiveProductType, userEmail, userType
      );

      if (!result?.success || !result?.data) {
        setDataLoadAttempted(currentTab, true);
        return;
      }
      const { metadata, data } = result;
      const swRows = Array.isArray(data)
        ? data
        : (data?.SW ?? data?.changeList ?? []);
      const preSelected = swRows
        .filter(r => String(r.applied || '').trim().toUpperCase() === 'YES')
        .map(r => ({ id: r.id, detail: r.detail, applied: true }));

      const preSelectedChanges = preSelected.length > 0 ? { SW: preSelected } : {};
      const normalizedData     = { SW: swRows };

      setChangeList(
        currentTab,
        metadata.id,
        { ...normalizedData, loadedProductType: effectiveProductType },
        preSelectedChanges,
        metadata?.source || 'default',
        metadata?.lastModified || null,
        metadata?.uploadDate || null
      );

      setSubTypeStatus(prev => ({
        ...prev,
        [effectiveProductType]: true,
        ...(currentTab === 'PD' ? { PD: true } : {}),
        ...(currentTab === 'AVA' ? { AVA: true } : {})
      }));

      if (currentTab === 'PD' || currentTab === 'AVA') {
        setSelectedSubType(prev => ({
          ...prev,
          [currentTab]: effectiveProductType
        }));
      }

      console.log(`Loaded ${effectiveProductType} from ${metadata?.source}`);

    } catch (err) {
      console.error(`Error loading SW data for ${effectiveProductType}:`, err);
      setLoadingError(`Failed to load data: ${err.message}`);
      setDataLoadAttempted(currentTab, true);
    }
  };

  const tid = setTimeout(loadDataForTab, 100);
  return () => clearTimeout(tid);
}, [
  userId,
  currentTab,
  currentSubType,
  location.state?.changeListId,
  location.state?.returnFromImport,
  location.state?.refreshKey,
  location.state?.forceDefaultTemplate
]);
  useEffect(() => {
  console.log('subTypeStatus =>', subTypeStatus);
}, [subTypeStatus]);


useEffect(() => {
  const { state } = location;
  if (!state) return;

  const {
  selectedChanges: newSelectedChanges,
  activeTab: newTab = currentTab,
  referenceModels: newReferenceModels,
  changeListId,
  completedItems = [],
  returnFromImport = false,
  subType: incomingSubType,
  isDefaultTemplate = false,   // ← ADD
} = state;

  console.log('SW Navigation state:', { newTab, changeListId, returnFromImport, incomingSubType });

  if (incomingSubType && tabs.find(t => t.id === newTab)?.hasSubTypes) {
    setSelectedSubType(prev => ({ ...prev, [newTab]: incomingSubType }));
  }

  if (newTab && newTab !== currentTab) {
    setCurrentTab(newTab);
  }

  const clearReturnFlag = () => {
    // Consume returnFromImport so a stale flag can't block the next
    // tab/subtype switch on this page.
    navigate(location.pathname, {
      replace: true,
      state: { ...state, returnFromImport: false }
    });
  };
      if (returnFromImport && changeListId) {
      const loadFromImport = async () => {
        try {
          const decoded = decodedToken();
          const userEmail = decoded?.email ?? '';
          const userType = decoded?.userType ?? 'Admin';
          const result = await getSWImportChangeListById(changeListId, userEmail, userType);

          if (result.success && result.data) {
            const { metadata, data } = result;
            const swRows = Array.isArray(data) ? data : (data?.SW ?? data?.changeList ?? []);

            const preSel = swRows
              .filter(r => String(r.applied || '').trim().toUpperCase() === 'YES')
              .map(r => ({ id: r.id, detail: r.detail, applied: true }));

            const preSelectedChanges = preSel.length > 0 ? { SW: preSel } : {};
            const normalizedData = { SW: swRows };

            setChangeList(newTab, metadata.id, normalizedData, preSelectedChanges, 'import', incomingSubType);

          // Keep "has data" status in sync with loadDataForTab's success path,
          // otherwise PD/AVA tab dots and isReviewEnabled won't reflect the import.
          const effectiveType = incomingSubType || newTab;
          setSubTypeStatus(prev => ({
            ...prev,
            [effectiveType]: true,
            ...(newTab === 'PD' ? { PD: true } : {}),
            ...(newTab === 'AVA' ? { AVA: true } : {})
          }));
        }
      } catch (error) {
        console.error('Failed to load from ImportSWChangeList:', error);
        setLoadingError(`Failed to load imported data: ${error.message}`);
      } finally {
        clearReturnFlag();
      }
    };

    loadFromImport();
  } else if (returnFromImport) {
    // returnFromImport true but no changeListId — still consume it
    // so it can't block subsequent tab switches.
    clearReturnFlag();
  }

  if (newSelectedChanges && Object.keys(newSelectedChanges).length > 0) {
    setSelectedChanges(newTab, newSelectedChanges);
  }

  if (newReferenceModels && Object.keys(newReferenceModels).length > 0) {
    Object.entries(newReferenceModels).forEach(([item, value]) => {
      setReferenceModel(newTab, item, value);
    });
  }

  completedItems.forEach(item => setCompletedItem(newTab, item));
}, [location.state]);


useEffect(() => {
  console.log('AVA status', {
    AVA: subTypeStatus['AVA'],
    AVA_Audio: subTypeStatus['AVA_Audio'],
    AVA_Headphone: subTypeStatus['AVA_Headphone']
  });
}, [subTypeStatus]);

const handleTabChange = (tabId) => {
  if (tabId === currentTab) return;

  setLoadingError('');

  if (tabs.find(t => t.id === tabId)?.hasSubTypes) {

    const subtypeWithData =
      subProductTypes[tabId]?.find(
        st => subTypeStatus[st.id]
      );

    if (subtypeWithData) {
      setSelectedSubType(prev => ({
        ...prev,
        [tabId]: subtypeWithData.id
      }));
    }

    setShowSubTypeDropdown(prev => ({
      ...prev,
      [tabId]: true
    }));
  }

  setCurrentTab(tabId);
};

const handleSubTypeChange = (
  tabId,
  subTypeId
) => {

  setSelectedSubType(prev => ({
    ...prev,
    [tabId]: subTypeId
  }));

  setShowSubTypeDropdown(prev => ({
    ...prev,
    [tabId]: false
  }));

  setLoadingError('');

  navigate(location.pathname, {
    replace: true,
    state: {
      ...location.state,
      activeTab: tabId,
      subType: subTypeId
    }
  });
};


const hasPDData =
  subTypeStatus['PD_Signage & IWB'] ||
  subTypeStatus['PD_RGB LED'] ||
  subTypeStatus['PD_AIO'] ||
  subTypeStatus['PD_EPD'];

const hasAVAData =
  subTypeStatus['AVA_Audio'] ||
  subTypeStatus['AVA_Headphone'];

  const handleItemClick = () => {
    if (!isDataLoaded(currentTab)) {
      alert('Please import a SW change list first.');
      return;
    }
    navigate(`/sw-assessment/${currentTab}/SW`, {
      state: {
        selectedChanges: currentTabData.selectedChanges,
        referenceModels: currentTabData.referenceModels,
        changeListId: currentTabData.changeListId,
        completedItems: currentTabData.completedItems instanceof Set
          ? Array.from(currentTabData.completedItems)
          : [],
        subType: currentSubType
      }
    });
  };

  const handleBackClick = () => {
    clearAllData();
    navigate('/dashboard');
  };

  const handleImportClick = () => {
    navigate('/import-sw-change-list', {
      state: {
        returnTab: currentTab,
        selectedChanges: currentTabData.selectedChanges || {},
        referenceModels: currentTabData.referenceModels || {},
        completedItems: currentTabData.completedItems instanceof Set
          ? Array.from(currentTabData.completedItems)
          : [],
        subType: currentSubType
      }
    });
  };

  const handleReviewClick = () => {
    if (!isReviewEnabled) {
      if (!isDataLoaded(currentTab)) {
        alert('Please import a SW change list first.');
      } else if (!hasSelectedChanges) {
        alert('Please select at least one change item before creating verification cases.');
      }
      return;
    }

    navigate('/sw-verification-review', {
      state: {
        selectedChanges: currentTabData.selectedChanges || {},
        activeTab: currentTab,
        referenceModels: currentTabData.referenceModels || {},
        changeListId: currentTabData.changeListId,
        changeListData: currentTabData.changeListData,
        userId,
        subType: currentSubType
      }
    });
  };

  const loading = isLoading(currentTab);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center">
            <button
              onClick={handleBackClick}
              className="flex items-center justify-center w-10 h-10 -ml-4 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors duration-200 touch-manipulation"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-white hover:text-white transition-colors" />
            </button>
            <h1 className="text-base font-medium sm:text-lg">SW Change Assessment</h1>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 sm:px-6 sm:py-6">
        {/* Title */}
        <div className="mb-6">
          <h2 className="mb-3 text-xl font-normal text-white sm:text-2xl md:text-3xl">
            SW Change Verification Assessment
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed sm:text-base max-w-4xl">
            Review changes in the SW Change List to generate a recommended validation checklist.
          </p>

          <div className="mt-4 space-y-2">
            {loading && (
              <div className="p-3 bg-blue-900/50 border border-blue-500 rounded-lg flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-300 flex-shrink-0 animate-spin" />
                <p className="text-blue-300 text-sm">Loading data for {currentSubType || currentTab}...</p>
              </div>
            )}
            {loadingError && (
              <div className="p-2 bg-red-900/50 border border-red-500 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0" />
                <p className="text-red-300 text-sm">{loadingError}</p>
              </div>
            )}
            {!userId && (
              <div className="p-3 border border-yellow-500 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                <p className="text-yellow-300 text-sm">User authentication required to load/save data.</p>
              </div>
            )}
            {!loading &&
              currentTabData &&
              currentTabData.dataLoaded &&
              currentTabData.selectedChanges &&
              !hasSelectedChanges && (
                <div className="p-1 bg-yellow-900/50 border border-yellow-500 rounded-lg flex items-center gap-2">
                  <p className="text-yellow-300 text-sm">
                    No change applied. Please select at least one change item to review and create verification cases.
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Product Tabs */}
        <div className="mb-3 sm:mb-4">
          <div className="flex border-b border-gray-700 overflow-x-auto">
            {tabs.map((tab, index) => {
              const tabIsLoading = isLoading(tab.id);
             let tabHasData = !!getTabData(tab.id)?.changeListData;

              if (tab.id === 'PD') {
                tabHasData = hasPDData;
              }

              if (tab.id === 'AVA') {
                tabHasData = hasAVAData;
              }
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  disabled={tabIsLoading}
                  className={`flex-shrink-0 min-w-0 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 touch-manipulation relative
                    ${currentTab === tab.id ? 'border-blue-500 text-white bg-blue-500/10' : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/50'}
                    ${tabIsLoading ? 'opacity-50 cursor-not-allowed' : ''}
                    ${index < tabs.length - 1 ? 'mr-2 sm:mr-6' : ''}
                    focus:outline-none min-h-[48px] flex items-center justify-center`}
                  aria-selected={currentTab === tab.id}
                  role="tab"
                >
                  <span className="whitespace-nowrap px-2">{tab.label}</span>
                  
                  {tabIsLoading && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-black animate-pulse" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub-type Dropdown for PD and AVA (Same as HW) */}
        {tabs.find(t => t.id === currentTab)?.hasSubTypes && (
          <div className="mb-4 ml-5 sm:ml-3">
            <div className="relative inline-block w-64">
              <button
                onClick={() => setShowSubTypeDropdown(prev => ({ ...prev, [currentTab]: !prev[currentTab] }))}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
              >
                <span className="text-sm">
                  {subProductTypes[currentTab]?.find(st => st.id === currentSubType)?.label || 'Select Type'}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showSubTypeDropdown[currentTab] ? 'rotate-180' : ''}`} />
              </button>

              {showSubTypeDropdown[currentTab] && (
                <div className="absolute z-30 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-auto">
                  {subProductTypes[currentTab]?.map(subType => (
                    <button
                      key={subType.id}
                      onClick={() => handleSubTypeChange(currentTab, subType.id)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 transition-colors ${currentSubType === subType.id ? 'bg-blue-600 text-white' : 'text-gray-300'
                        }`}
                    >
                      {subType.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Current tab / sub-type label */}
        <div className="ml-5 sm:ml-3 sm:mb-1">
          <h3 className="text-lg font-normal text-white sm:text-xl md:text-2xl">
            {currentSubType || currentTab}
          </h3>
        </div>

        {/* Assessment Item */}
        <div className="p-3 mb-8 space-y-1 sm:space-y-0 pb-30">
          {assessmentItems.map((item) => {
            const hasData = !!currentTabData?.changeListData;
            const isCompleted = currentTabData.completedItems instanceof Set
              ? currentTabData.completedItems.has(item)
              : false;

            return (
              <button
                key={`${currentTab}-${item}`}
                onClick={handleItemClick}
               // disabled={!isDataLoaded(currentTab) || loading}
                disabled={!hasData || loading}
                className={`group flex items-center justify-between w-full py-2 px-2 sm:px-0 sm:py-3 border-b border-gray-800 last:border-b-0 hover:bg-gray-900/50 active:bg-gray-800/70 focus:outline-none transition-all duration-200 touch-manipulation rounded-lg sm:rounded-none min-h-[56px] sm:min-h-[64px] ${(!isDataLoaded(currentTab) || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="button"
                aria-label={`Open ${item} assessment`}
              >
                <span className="text-base font-normal text-white sm:text-lg md:text-xl group-hover:text-blue-400 transition-colors duration-200">
                  {item}
                </span>
                <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12">
                  <CheckIcon
                    className={`w-7 h-7 sm:w-8 sm:h-8 transition-all duration-200 ${isCompleted || hasData ? 'text-green-400 scale-110' : 'text-gray-400 group-hover:text-blue-400'}`}
                    style={{ strokeWidth: 2.5 }}
                  />
                </div>
              </button>
            );
          })}

        </div>

        {/* Bottom Action Buttons */}
        <div className="fixed inset-x-0 bottom-0 z-40 bg-black pt-4 pb-4 space-y-3 sm:space-y-0 sm:flex sm:justify-end sm:gap-4 px-4">
          <button
            onClick={handleImportClick}
            disabled={loading}
            className="w-full sm:w-auto px-6 sm:py-3 py-2 text-white bg-gray-600 rounded-lg hover:bg-gray-700 active:bg-gray-800 transition-all duration-200 touch-manipulation min-h-[40px] font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Import SW Change List
          </button>
          <button
            className={`w-full sm:w-auto px-6 sm:py-3 py-2 text-white rounded-lg font-medium text-sm sm:text-base min-h-[40px] transition-all duration-200 touch-manipulation ${isReviewEnabled ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-lg hover:shadow-xl' : 'bg-blue-600/50 cursor-not-allowed'}`}
            type="button"
            disabled={!isReviewEnabled}
            onClick={handleReviewClick}
          >
            Review and Create Verification Cases
          </button>
        </div>
      </div>
    </div>
  );
};

export default SWChangeVerification;






